import React, { useCallback, useRef, useState } from 'react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

/**
 * A run was refused only because its eSIM has to move to another modem and
 * the eSIM has a limited number of downloads (#375). executeFlow answers 409
 * with `approvalRequired: true`; the caller asks the user and resubmits with
 * `executionContext.allowEsimRelocation: true`.
 */
export function isEsimMoveApprovalError(err: unknown): err is Error & { approvalRequired: true } {
  return !!err && (err as any).approvalRequired === true;
}

/** The neutral note to show when the user declines the move. */
export const ESIM_MOVE_DECLINED = 'Not run: the eSIM stays on the modem that holds it.';

/**
 * `confirmMove(message)` opens the prompt and resolves true (approved) or
 * false. Concurrent calls (a fan-out across locations) queue up and are asked
 * one at a time. Render `dialog` once in the calling component.
 */
export function useEsimMoveApproval(): {
  confirmMove: (message: string) => Promise<boolean>;
  dialog: React.ReactElement;
} {
  const [pending, setPending] = useState<{ message: string; resolve: (ok: boolean) => void } | null>(null);
  const chain = useRef<Promise<unknown>>(Promise.resolve());

  const confirmMove = useCallback((message: string) => {
    const asked = chain.current.then(
      () => new Promise<boolean>((resolve) => setPending({ message, resolve })),
    );
    chain.current = asked;
    return asked;
  }, []);

  const answer = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  const dialog = (
    <ConfirmDialog
      open={!!pending}
      onClose={() => answer(false)}
      onConfirm={() => answer(true)}
      title="Move this eSIM to another modem?"
      description={
        <div className="space-y-2">
          {/* The server's refusal ends with how to approve via the test setting
              or API; here the buttons are the approval, so drop that part. */}
          <p>{pending?.message.replace(/,? so it needs approval:.*$/s, '.')}</p>
          <p>
            Moving it deletes the eSIM from the modem that holds it and downloads it onto the modem
            that runs this test. That uses one of its limited downloads and cannot be undone.
          </p>
        </div>
      }
      confirmText="Move and run"
      cancelText="Don't run"
      variant="warning"
    />
  );

  return { confirmMove, dialog };
}
